import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureAttributes } from "../../../modules/FeatureCard/hooks/useFeatureAttributes";
import { useFeatureCardModule } from "../../../modules/FeatureCard/hooks/useFeatureCardModule";
import { useResetFeatureSelect } from "../../../modules/FeatureSelector/hooks/useResetFeatureSelect";
import { useDialogInfo, useDialogReset } from "../../../hooks/useDialogs";
import { FeatureCardMode } from "../../../modules/FeatureCard/types";

export const useCloseFeatureCard = () => {
  const { t } = useTranslation("dialogs");

  const {
    resources: [fallback],
  } = useDialogInfo(true);

  const { feature, mode, isEdit } = useFeatureCardModule();

  const dialogMessage: Record<FeatureCardMode, string> = useMemo(
    () => ({
      [FeatureCardMode.Edit]: t("closeFeatureCard.saveChanges"),
      [FeatureCardMode.Create]: t("closeFeatureCard.saveObject"),
    }),
    [t],
  );

  const resetFeatureSelect = useResetFeatureSelect();
  const goBack = useDialogReset();

  const [, { saveAttributes }] = useFeatureAttributes(feature, isEdit, mode, fallback);

  const close = useCallback(
    (withSave?: boolean) => {
      goBack();
      resetFeatureSelect(withSave);
    },
    [goBack, resetFeatureSelect, fallback],
  );

  const save = useCallback(() => {
    close(true);
    saveAttributes();

    if (mode === FeatureCardMode.Edit) {
      typeof fallback === "function" && fallback();
    }
  }, [saveAttributes, close]);

  const dontSave = useCallback(() => {
    close();

    typeof fallback === "function" && fallback();
  }, [close, fallback]);

  return {
    message: dialogMessage[mode],
    goBack,
    save,
    dontSave,
  };
};
